import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { crewService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";

interface CrewDocumentFormProps {
  document?: {
    id: string;
    name: string;
    url: string;
    type: string;
    uploaded_at: string;
  }; // If provided, we're editing an existing document
  crewMemberId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function CrewDocumentForm({
  document,
  crewMemberId,
  onSuccess,
  onCancel,
}: CrewDocumentFormProps) {
  const { user, currentOrganization } = useAuth();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const isEditing = !!document;

  const [formData, setFormData] = useState({
    name: document?.name || "",
    type: document?.type || "identification",
    url: document?.url || "",
  });

  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const uploadDocument = async (): Promise<string> => {
    if (!file) return formData.url; // If no new file, keep existing URL
    if (!user || !currentOrganization)
      throw new Error("User or organization not found");

    setIsUploading(true);
    try {
      return await crewService.uploadDocument(
        file,
        user.id,
        currentOrganization.id
      );
    } catch (error) {
      console.error("Error uploading document:", error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const documentMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("User not authenticated");
      if (!currentOrganization) throw new Error("No organization selected");

      try {
        // Upload document if there's a file
        const documentUrl = await uploadDocument();

        // Get the current crew member data
        const { data: crewMember } = await crewService.getCrewMember(
          crewMemberId,
          currentOrganization.id
        );

        if (!crewMember) {
          throw new Error("Crew member not found");
        }

        // Prepare the updated documents array
        let updatedDocuments = [...(crewMember.documents || [])];

        if (isEditing && document) {
          // Update existing document
          updatedDocuments = updatedDocuments.map((doc) =>
            doc.id === document.id
              ? {
                  ...doc,
                  name: formData.name,
                  type: formData.type,
                  url: documentUrl,
                }
              : doc
          );
        } else {
          // Add new document
          updatedDocuments = [
            ...updatedDocuments,
            {
              id: `doc-${Date.now()}`,
              name: formData.name,
              type: formData.type,
              url: documentUrl,
              uploaded_at: new Date().toISOString(),
            },
          ];
        }

        // Update the crew member with the new documents array
        return await crewService.updateCrewMember(
          crewMemberId,
          {
            documents: updatedDocuments,
            updated_by: user.id,
          },
          currentOrganization.id
        );
      } catch (error: any) {
        setError(error.message || "Failed to save document");
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["crew", crewMemberId] });
      if (onSuccess) onSuccess();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.name) {
      setError("Document name is required");
      return;
    }

    if (!formData.url && !file) {
      setError("Please select a file or provide a URL");
      return;
    }

    documentMutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-md">{error}</div>
      )}

      {/* Document Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Document Name
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g., Passport, Driver's License, Certificate"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          required
        />
      </div>

      {/* Document Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Document Type
        </label>
        <select
          name="type"
          value={formData.type}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="identification">Identification</option>
          <option value="certification">Certification</option>
          <option value="contract">Contract</option>
          <option value="insurance">Insurance</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* File Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {isEditing ? "Replace File" : "Upload File"}
        </label>
        <input
          type="file"
          accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
          onChange={handleFileChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
        />
        {isEditing && (
          <p className="text-xs text-gray-500 mt-1">
            Leave empty to keep the current file
          </p>
        )}
      </div>

      {/* Document URL (for external documents) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Or External URL
        </label>
        <input
          type="url"
          name="url"
          value={formData.url}
          onChange={handleChange}
          placeholder="https://example.com/document.pdf"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <p className="text-xs text-gray-500 mt-1">
          You can provide a URL instead of uploading a file
        </p>
      </div>

      {/* Current Document (if editing) */}
      {isEditing && formData.url && (
        <div className="p-3 bg-gray-50 rounded-md">
          <p className="text-sm font-medium text-gray-700">Current Document:</p>
          <a
            href={formData.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline"
          >
            View Document
          </a>
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="px-4 py-2 text-white bg-primary rounded-md hover:bg-primary/90 flex items-center"
          disabled={isUploading || documentMutation.isPending}
        >
          {isUploading || documentMutation.isPending ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {isUploading ? "Uploading..." : "Saving..."}
            </>
          ) : (
            <>{isEditing ? "Update Document" : "Add Document"}</>
          )}
        </button>
      </div>
    </form>
  );
}
